import { formatDate } from '@/utils/date';
import type { DateFilterModel, NumberFilterModel, SetFilterModel, TextFilterModel } from 'ag-grid-community';
import type { JSX } from 'react';
import { FormattedMessage } from 'react-intl';

export type HandledFilterModel =
  | TextFilterModel
  | DateFilterModel
  | SetFilterModel
  | NumberFilterModel;

type FilterModelType = Required<HandledFilterModel>['filterType'];
type Formatter = (model: HandledFilterModel) => JSX.Element;

export const formatters: { [filterType in FilterModelType]: Formatter } = {
  text: textFormatter,
  date: dateFormatter,
  set: setFormatter,
  number: numberFormatter,
};

export const ErrorIcon = () => <em className="icon icon-sm">error</em>;

function textFormatter(model: HandledFilterModel) {
  if (model.filterType !== 'text') {
    return <ErrorIcon />;
  }

  let formatted: JSX.Element;
  switch (model.type) {
    case 'contains':
      formatted = (
        <FormattedMessage id="filter.text.contains" values={{ value: model.filter ?? '<empty>' }} />
      );
      break;
    default:
      formatted = <ErrorIcon />;
      break;
  }

  return formatted;
}

function dateFormatter(model: HandledFilterModel) {
  if (model.filterType !== 'date') {
    return <ErrorIcon />;
  }

  let formatted: JSX.Element;
  switch (model.type) {
    case 'equals': // no break
    case 'greaterThan': // no break
    case 'lessThan':
      formatted = model.dateFrom ? (
        <FormattedMessage
          id={`filter.date.${model.type}`}
          values={{
            date: formatDate(model.dateFrom),
          }}
        />
      ) : (
        <ErrorIcon />
      );
      break;
    case 'inRange':
      formatted =
        model.dateFrom && model.dateTo ? (
          <FormattedMessage
            id="filter.date.inRange"
            values={{
              from: formatDate(model.dateFrom),
              to: formatDate(model.dateTo),
            }}
          />
        ) : (
          <ErrorIcon />
        );

      break;
    default:
      formatted = <ErrorIcon />;
      break;
  }

  return formatted;
}

function setFormatter(model: HandledFilterModel) {
  if (model.filterType !== 'set') {
    return <ErrorIcon />;
  }

  let formatted: JSX.Element;
  switch (model.filterType) {
    case 'set':
      formatted = (
        <FormattedMessage
          id="filter.set.isAmongst"
          values={{ value: model.values.length ? model.values.join(', ') : '<empty>' }}
        />
      );
      break;
    default:
      formatted = <ErrorIcon />;
      break;
  }

  return formatted;
}

function numberFormatter(model: HandledFilterModel) {
  if (model.filterType !== 'number') {
    return <ErrorIcon />;
  }

  return (
    <FormattedMessage id={`filter.number.${model.type}`} values={model as Record<string, string>} />
  );
}
